// Firebase Analytics - Configuration complète pour tous les événements
// Firebase Analytics est intégré avec Google Analytics 4
import { analytics, initAnalytics } from '../config/firebase';
import { logEvent } from 'firebase/analytics';
import { HOME_ROUTE_KEY } from '../utils/sessionStorageKeys';

// Variable pour stocker l'instance analytics une fois initialisée
let analyticsInstance = null;

// Obtenir l'instance analytics
const getAnalyticsInstance = () => {
  // Si analytics est déjà disponible, l'utiliser
  if (analytics) {
    analyticsInstance = analytics;
    return analyticsInstance;
  }
  
  // Sinon, essayer d'initialiser
  if (!analyticsInstance && typeof window !== 'undefined') {
    initAnalytics().then((instance) => {
      if (instance) {
        analyticsInstance = instance;
      }
    });
  }
  
  return analyticsInstance;
};

// Récupérer la page d'accueil d'origine (/ ou /onSite) pour segmenter les utilisateurs
const getHomeOrigin = () => {
  try {
    const route = typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem(HOME_ROUTE_KEY)
      : null;
    if (route === '/') return 'main';
    if (route === '/onSite') return 'onSite';
    return 'unknown';
  } catch {
    return 'unknown';
  }
};

// Fonction principale pour tracker un événement
export const trackEvent = (eventName, eventParams = {}) => {
  const instance = getAnalyticsInstance();
  const homeOrigin = getHomeOrigin();

  if (instance) {
    try {
      logEvent(instance, eventName, {
        ...eventParams,
        home_origin: homeOrigin,
        timestamp: new Date().toISOString(),
      });
      
      // Log en développement pour déboguer (sauf page_view et session_start)
      const silentEvents = ['page_view', 'session_start'];
      if (process.env.NODE_ENV === 'development' && !silentEvents.includes(eventName)) {
        console.log('📊 Analytics Event:', eventName, eventParams);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Erreur lors du tracking Firebase Analytics:', error);
      }
    }
  } else {
    // Si l'instance n'est pas encore disponible, attendre un peu puis réessayer
    setTimeout(() => {
      const retryInstance = getAnalyticsInstance();
      if (retryInstance) {
        try {
          logEvent(retryInstance, eventName, {
            ...eventParams,
            home_origin: getHomeOrigin(),
            timestamp: new Date().toISOString(),
          });
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Erreur lors du tracking Firebase Analytics:', error);
          }
        }
      }
    }, 500);
  }
};

// ==========================================
// ÉVÉNEMENTS DE SESSION ET NAVIGATION
// ==========================================

// Suivre une vue de page
export const trackPageView = (path) => {
  // Tracker avec Firebase Analytics
  trackEvent('page_view', {
    page_path: path,
    page_title: typeof document !== 'undefined' ? document.title : '',
    page_location: typeof window !== 'undefined' ? window.location.href : '',
  });
  
  // Tracker aussi avec gtag (Google Analytics direct)
  trackPageViewGtag(path, typeof document !== 'undefined' ? document.title : '');
};

// Suivre le début de session
export const trackSessionStart = () => {
  trackEvent('session_start', {
    event_category: 'engagement',
    event_label: 'New Session',
  });
};

// ==========================================
// ÉVÉNEMENTS DE NAVIGATION (HomePage)
// ==========================================

// Suivre le clic sur le bouton menu
export const trackMenuClick = () => {
  trackEvent('menu_button_click', {
    event_category: 'navigation',
    event_label: 'View Menu',
    button_location: 'homepage',
  });
};

// Suivre le clic sur le bouton réservation
export const trackReservationButtonClick = () => {
  trackEvent('reservation_button_click', {
    event_category: 'navigation',
    event_label: 'View Reservation Form',
    button_location: 'homepage',
  });
};

// Suivre le clic sur le bouton directions (Google Maps)
export const trackDirectionsClick = () => {
  trackEvent('directions_click', {
    event_category: 'navigation',
    event_label: 'Get Directions to Restaurant',
    destination: 'Aqua Farina Restaurant',
    button_location: 'homepage',
  });
};

// Suivre le clic sur le numéro de téléphone
export const trackPhoneClick = () => {
  trackEvent('phone_click', {
    event_category: 'contact',
    event_label: 'Call Restaurant',
    phone_number: '+21626773104',
    button_location: 'homepage',
  });
};

// ==========================================
// ÉVÉNEMENTS DE RÉSERVATION
// ==========================================

// Suivre une réservation soumise
export const trackReservation = (reservationData) => {
  trackEvent('reservation_submitted', {
    event_category: 'engagement',
    event_label: 'Reservation Completed',
    reservation_name: reservationData.name || 'Anonymous',
    reservation_date: reservationData.date,
    reservation_time: reservationData.time,
    reservation_persons: reservationData.persons || '2',
    has_note: !!reservationData.note,
    value: parseInt(reservationData.persons) || 2,
  });
};

// Suivre le début d'une réservation (ouverture du formulaire)
export const trackReservationStart = () => {
  trackEvent('reservation_start', {
    event_category: 'engagement',
    event_label: 'Reservation Form Opened',
  });
};

// ==========================================
// ÉVÉNEMENTS DE LANGUE
// ==========================================

// Suivre la sélection de langue (pour analyser quelle langue est la plus utilisée)
export const trackLanguageSelection = (language) => {
  const languageNames = {
    'fr': 'Français',
    'en': 'English',
    'it': 'Italiano',
    'ar': 'العربية'
  };
  
  trackEvent('language_selected', {
    event_category: 'user_preference',
    event_label: languageNames[language] || language,
    language_code: language,
    language_name: languageNames[language] || language,
    value: 1, // Pour faciliter le comptage dans GA4
  });
};

// ==========================================
// ÉVÉNEMENTS DE MENU
// ==========================================

// Suivre le temps passé sur la page menu
export const trackTimeOnPage = (pageName, timeInSeconds) => {
  trackEvent('time_on_page', {
    event_category: 'engagement',
    event_label: 'Time Spent on Page',
    page_name: pageName,
    time_seconds: Math.round(timeInSeconds),
    value: Math.round(timeInSeconds),
  });
};

// Suivre le changement d'onglet dans le menu
export const trackMenuTabChange = (tabName) => {
  trackEvent('menu_tab_change', {
    event_category: 'engagement',
    event_label: 'Menu Tab Changed',
    tab_name: tabName,
    menu_type: tabName === 'pizza' ? 'Pizza Menu' : 'Main Menu',
  });
};

// Suivre un clic sur un élément du menu (optionnel, si vous voulez tracker les plats populaires)
export const trackMenuItemView = (itemName, category) => {
  trackEvent('menu_item_view', {
    event_category: 'engagement',
    event_label: 'Menu Item Viewed',
    item_name: itemName,
    item_category: category,
  });
};

// ==========================================
// ÉVÉNEMENTS D'ENGAGEMENT
// ==========================================

// Suivre l'interaction avec la carte Google Maps (iframe)
export const trackMapInteraction = () => {
  trackEvent('map_interaction', {
    event_category: 'engagement',
    event_label: 'Google Maps Interaction',
    map_type: 'embedded_iframe',
  });
};

// Suivre le scroll jusqu'à la section contact/maps
export const trackScrollToContact = () => {
  trackEvent('scroll_to_contact', {
    event_category: 'engagement',
    event_label: 'Scrolled to Contact Section',
  });
};

// ==========================================
// ÉVÉNEMENTS D'ERREUR (optionnel)
// ==========================================

// Suivre les erreurs d'envoi d'email
export const trackEmailError = (errorMessage) => {
  trackEvent('email_error', {
    event_category: 'error',
    event_label: 'Reservation Email Failed',
    error_message: errorMessage,
  });
};

// Suivre les erreurs de formulaire
export const trackFormError = (formType, errorMessage) => {
  trackEvent('form_error', {
    event_category: 'error',
    event_label: 'Form Validation Error',
    form_type: formType,
    error_message: errorMessage,
  });
};

// ==========================================
// INITIALISATION
// ==========================================

// Initialiser Firebase Analytics (pour compatibilité avec le code existant)
export const initGA = () => {
  // Initialiser Analytics et tracker la page actuelle
  if (typeof window !== 'undefined') {
    initAnalytics().then((instance) => {
      if (instance) {
        analyticsInstance = instance;
        trackPageView(window.location.pathname);
        trackSessionStart();
      }
    });
  }
};

// Exporter le Measurement ID Google Analytics
export const GA_MEASUREMENT_ID = 'G-965RLTBN2J';

// Fonction pour utiliser gtag directement (si disponible)
export const gtag = (...args) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag(...args);
  }
};

// Fonction pour tracker une page view avec gtag
export const trackPageViewGtag = (path, title) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: path,
      page_title: title || document.title,
    });
  }
};

// ==========================================
// DOCUMENTATION DES ÉVÉNEMENTS
// ==========================================
/*
LISTE COMPLÈTE DES ÉVÉNEMENTS TRACKÉS :

Tous les événements incluent home_origin pour segmenter les utilisateurs :
- main : utilisateur venu de la page d'accueil /
- onSite : utilisateur venu de la page /onSite
- unknown : accès direct à une autre page

1. SESSION & NAVIGATION
   - session_start : Début de session utilisateur
   - page_view : Vue de page (automatique)

2. NAVIGATION (HomePage)
   - menu_button_click : Clic sur "Menu"
   - reservation_button_click : Clic sur "Réservation"
   - directions_click : Clic sur "Naviguer au restaurant"
   - phone_click : Clic sur le numéro de téléphone

3. RÉSERVATION
   - reservation_start : Ouverture du formulaire de réservation
   - reservation_submitted : Soumission d'une réservation

4. LANGUE
   - language_selected : Changement de langue (automatique et manuel)

5. MENU
   - time_on_page : Temps passé sur la page menu
   - menu_tab_change : Changement d'onglet (Pizza / Menu Principal)
   - menu_item_view : Vue d'un plat du menu (optionnel)

6. ENGAGEMENT
   - map_interaction : Interaction avec la carte Google Maps
   - scroll_to_contact : Scroll jusqu'à la section contact

7. ERREURS
   - email_error : Erreur lors de l'envoi d'email
   - form_error : Erreur de validation de formulaire

Tous les événements sont trackés dans Google Analytics 4 via Firebase Analytics.
*/

